import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Subscription} from "rxjs";
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {ConfirmPasswordDirective} from "@app/_directives/confirm-password.directive";
import {NgOptimizedImage} from "@angular/common";
import {PasswordRequirementDirective} from "@app/_directives/password-requirement.directive";
import {LoginResponse, NewPasswordRequest} from "@app/_models/api";
import {AuthService} from "@app/_services/auth.service";
import {ToastService} from "@app/_services/toast.service";

interface NewPasswordForm {
    password: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
}

@Component({
    selector: 'app-new-password-required',
    imports: [NgOptimizedImage, ReactiveFormsModule],
    templateUrl: './new-password-required.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./new-password-required.component.scss']
})
export class NewPasswordRequiredComponent implements OnInit, OnDestroy {
    formGroup: FormGroup<NewPasswordForm>;
    loading = false;
    passwordFieldType: boolean = true;
    confirmPasswordFieldType: boolean = true;
    private routeSub: Subscription;
    private email: string | null | undefined;
    private session: string | null | undefined;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private confirmPasswordValidator: ConfirmPasswordDirective,
        private passwordRequirementValidator: PasswordRequirementDirective,
        private authService: AuthService,
        private toastService: ToastService
    ) {
        this.routeSub = this.activatedRoute.queryParamMap.subscribe(queryParams => {
            this.email = queryParams.get("email");
            this.session = queryParams.get("session");
        });

        this.formGroup = new FormGroup<NewPasswordForm>({
            password: new FormControl("", {validators: [Validators.required, this.passwordRequirementValidator.validate.bind(this.passwordRequirementValidator)]}),
            confirmPassword: new FormControl("", Validators.required),
        }, {validators: [this.confirmPasswordValidator.validate.bind(this.confirmPasswordValidator)]});
    }

    ngOnInit() {
        this.formGroup.reset();
    }

    ngOnDestroy() {
        this.routeSub.unsubscribe();
    }

    checkPasswordForError(error: string): boolean {
        const password = this.formGroup.controls.password.value;
        if (password == null || password == "") {
            return true;
        }

        if (!this.formGroup.controls.password.hasError('weakPassword')) {
            return false;
        }

        const errors: string[] = this.formGroup.controls.password.getError('weakPassword');
        return errors.some(value => value === error);
    }

    onSubmit() {
        if (this.formGroup.valid) {
            this.loading = true;
            const controls = this.formGroup.controls;

            const newPasswordRequest: NewPasswordRequest = {
                email: this.email!, password: controls['confirmPassword'].value!, session: this.session!,
            };

            this.authService.newPasswordChallenge(newPasswordRequest).subscribe({
                next: (response: LoginResponse) => {
                    this.loading = false;
                    this.authService.handleSuccessfulLogin(response);
                    this.router.navigateByUrl('/dashboard').then();
                }, error: () => {
                    this.loading = false;
                    this.toastService.showDanger("Request failed", "Please try again.");
                },
            });
        }

    }
}
